import React, { useState, useCallback } from 'react';
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Trash2, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { parseXlsxFile, convertToApiPayloads, ImportResult, ValidationError, ParsedPromocao } from '../utils/xlsxParser';
import { multipatrocinadorService } from '../services/multipatrocinadores';

interface ImportXlsxModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess?: () => void;
}

export const ImportXlsxModal: React.FC<ImportXlsxModalProps> = ({ isOpen, onClose, onImportSuccess }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
  const [sendResults, setSendResults] = useState<Array<{ idRegra: string; success: boolean; error?: string }>>([]);
  const [expandedErrors, setExpandedErrors] = useState(true);
  const [expandedPromocoes, setExpandedPromocoes] = useState(true);

  const resetState = () => {
    setFile(null);
    setImportResult(null);
    setSending(false);
    setSendProgress({ current: 0, total: 0 });
    setSendResults([]);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFile = useCallback(async (selectedFile: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExtension = validExtensions.some(ext => selectedFile.name.toLowerCase().endsWith(ext));

    if (!validTypes.includes(selectedFile.type) && !hasValidExtension) {
      setImportResult({
        promocoes: [],
        errors: [{ field: 'arquivo', message: 'Formato de arquivo inválido. Use XLSX, XLS ou CSV.', severity: 'error' }],
        totalRows: 0,
        validRows: 0,
      });
      return;
    }

    setFile(selectedFile);
    setParsing(true);
    setImportResult(null);
    setSendResults([]);

    try {
      const result = await parseXlsxFile(selectedFile);
      setImportResult(result);
    } catch (err) {
      setImportResult({
        promocoes: [],
        errors: [{ field: 'arquivo', message: (err as Error).message, severity: 'error' }],
        totalRows: 0,
        validRows: 0,
      });
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFile(droppedFile);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleFile(selectedFile);
    e.target.value = '';
  }, [handleFile]);

  const hasErrors = importResult?.errors.some(e => e.severity === 'error') || false;

  const handleImport = async () => {
    if (!importResult || hasErrors) return;

    const payloads = convertToApiPayloads(importResult.promocoes);
    setSending(true);
    setSendProgress({ current: 0, total: payloads.length });
    const results: Array<{ idRegra: string; success: boolean; error?: string }> = [];

    for (let i = 0; i < payloads.length; i++) {
      const payload = payloads[i];
      try {
        const response = await multipatrocinadorService.saveConfiguration(payload.idRegraPromocao, payload);
        results.push({
          idRegra: payload.idRegraPromocao,
          success: response.success,
          error: response.success ? undefined : (response.error || 'Erro desconhecido'),
        });
      } catch (err) {
        results.push({
          idRegra: payload.idRegraPromocao,
          success: false,
          error: (err as Error).message,
        });
      }
      setSendProgress({ current: i + 1, total: payloads.length });
      setSendResults([...results]);
    }

    setSending(false);

    const allSuccess = results.every(r => r.success);
    if (allSuccess && onImportSuccess) {
      onImportSuccess();
    }
  };

  const getSegmentacaoColor = (tipo: string) => {
    switch (tipo) {
      case 'IDRegra': return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' };
      case 'BU': return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
      case 'EAN': return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
    }
  };

  const renderPromoPreview = (promo: ParsedPromocao) => {
    const colors = getSegmentacaoColor(promo.tipoSegmentacao);
    return (
      <div key={promo.idRegra} className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-bold text-gray-900">IDRegra: {promo.idRegra}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border}`}>
              {promo.tipoSegmentacao}
            </span>
            <span className="text-xs text-gray-500">
              {promo.rows.length} {promo.rows.length === 1 ? 'linha' : 'linhas'}
            </span>
          </div>
          {promo.totalBudget > 0 && (
            <span className="text-sm font-semibold text-green-700">
              Budget: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(promo.totalBudget)}
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50/50">
                {promo.tipoSegmentacao === 'BU' && <th className="px-3 py-2 text-left font-medium text-gray-600">BU</th>}
                {promo.tipoSegmentacao === 'EAN' && <th className="px-3 py-2 text-left font-medium text-gray-600">EAN</th>}
                {promo.tipoSegmentacao !== 'EAN' && <th className="px-3 py-2 text-right font-medium text-gray-600">Budget</th>}
                <th className="px-3 py-2 text-right font-medium text-blue-600">% Indústria.</th>
                <th className="px-3 py-2 text-right font-medium text-green-600">% Distribuidor</th>
                <th className="px-3 py-2 text-right font-medium text-orange-600">% Marketplace</th>
                <th className="px-3 py-2 text-right font-medium text-rose-600">% Executivo</th>
              </tr>
            </thead>
            <tbody>
              {promo.rows.map((row, idx) => (
                <tr key={idx} className="border-t border-gray-100">
                  {promo.tipoSegmentacao === 'BU' && <td className="px-3 py-2 font-medium">{row.bu}</td>}
                  {promo.tipoSegmentacao === 'EAN' && <td className="px-3 py-2 font-medium">{row.ean}</td>}
                  {promo.tipoSegmentacao !== 'EAN' && (
                    <td className="px-3 py-2 text-right">
                      {row.budget > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.budget) : '-'}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right text-blue-700">{row.pctIndustria || '-'}</td>
                  <td className="px-3 py-2 text-right text-green-700">{row.pctDistribuidor || '-'}</td>
                  <td className="px-3 py-2 text-right text-orange-700">{row.pctMarketplace || '-'}</td>
                  <td className="px-3 py-2 text-right text-rose-700">{row.pctExecutivo || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderErrors = (errors: ValidationError[]) => {
    const byIdRegra: Record<string, ValidationError[]> = {};
    const general: ValidationError[] = [];

    errors.forEach(err => {
      if (err.idRegra) {
        if (!byIdRegra[err.idRegra]) byIdRegra[err.idRegra] = [];
        byIdRegra[err.idRegra].push(err);
      } else {
        general.push(err);
      }
    });

    return (
      <div className="space-y-3">
        {general.length > 0 && (
          <div className="space-y-2">
            {general.map((err, i) => (
              <div key={i} className={`flex items-start space-x-2 p-3 rounded-lg ${
                err.severity === 'error' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
              }`}>
                <AlertCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                  err.severity === 'error' ? 'text-red-500' : 'text-amber-500'
                }`} />
                <div>
                  <span className={`text-sm font-medium ${
                    err.severity === 'error' ? 'text-red-700' : 'text-amber-700'
                  }`}>{err.field}</span>
                  <p className={`text-sm ${
                    err.severity === 'error' ? 'text-red-600' : 'text-amber-600'
                  }`}>{err.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {Object.entries(byIdRegra).map(([idRegra, errs]) => (
          <div key={idRegra} className="border border-red-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-bold text-red-700">IDRegra: {idRegra}</span>
              <span className="text-xs text-red-500">({errs.length} {errs.length === 1 ? 'erro' : 'erros'})</span>
            </div>
            <div className="p-3 space-y-2">
              {errs.map((err, i) => (
                <div key={i} className="flex items-start space-x-2">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                    err.severity === 'error' ? 'bg-red-500' : 'bg-amber-500'
                  }`} />
                  <p className={`text-sm ${
                    err.severity === 'error' ? 'text-red-600' : 'text-amber-600'
                  }`}>{err.message}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-5 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Importar XLSX</h2>
                <p className="text-emerald-100 text-sm">Importe promoções a partir de uma planilha</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {!file && !importResult && (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
                  isDragging
                    ? 'border-emerald-500 bg-emerald-50 scale-[1.02]'
                    : 'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/50'
                }`}
              >
                <Upload className={`h-12 w-12 mx-auto mb-4 ${isDragging ? 'text-emerald-500' : 'text-gray-400'}`} />
                <p className="text-lg font-semibold text-gray-700 mb-2">
                  Arraste o arquivo XLSX aqui
                </p>
                <p className="text-sm text-gray-500 mb-6">ou clique para selecionar</p>
                <label className="inline-flex items-center space-x-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold cursor-pointer hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-500/25">
                  <FileSpreadsheet className="h-5 w-5" />
                  <span>Selecionar Arquivo</span>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-400 mt-4">Formatos aceitos: XLSX, XLS, CSV</p>
              </div>
            )}

            {parsing && (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4" />
                <p className="text-gray-600 font-medium">Processando arquivo...</p>
              </div>
            )}

            {file && importResult && !parsing && (
              <>
                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
                    <div>
                      <p className="font-semibold text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {importResult.totalRows} linhas processadas | {importResult.promocoes.length} {importResult.promocoes.length === 1 ? 'promoção' : 'promoções'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={resetState}
                    disabled={sending}
                    className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Remover</span>
                  </button>
                </div>

                {importResult.errors.length > 0 && (
                  <div>
                    <button
                      onClick={() => setExpandedErrors(!expandedErrors)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <span className="font-bold text-red-700">
                          {importResult.errors.filter(e => e.severity === 'error').length} {importResult.errors.filter(e => e.severity === 'error').length === 1 ? 'erro encontrado' : 'erros encontrados'}
                        </span>
                      </div>
                      {expandedErrors ? <ChevronUp className="h-5 w-5 text-red-500" /> : <ChevronDown className="h-5 w-5 text-red-500" />}
                    </button>
                    {expandedErrors && (
                      <div className="mt-3">
                        {renderErrors(importResult.errors)}
                      </div>
                    )}
                  </div>
                )}

                {importResult.promocoes.length > 0 && (
                  <div>
                    <button
                      onClick={() => setExpandedPromocoes(!expandedPromocoes)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <span className="font-bold text-emerald-700">
                          {importResult.promocoes.length} {importResult.promocoes.length === 1 ? 'promoção identificada' : 'promoções identificadas'}
                        </span>
                      </div>
                      {expandedPromocoes ? <ChevronUp className="h-5 w-5 text-emerald-500" /> : <ChevronDown className="h-5 w-5 text-emerald-500" />}
                    </button>
                    {expandedPromocoes && (
                      <div className="mt-3 space-y-4">
                        {importResult.promocoes.map(promo => renderPromoPreview(promo))}
                      </div>
                    )}
                  </div>
                )}

                {sending && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-blue-700">Enviando promoções...</span>
                      <span className="text-sm text-blue-600">{sendProgress.current}/{sendProgress.total}</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(sendProgress.current / sendProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {sendResults.length > 0 && !sending && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">Resultado da importação</h4>
                    {sendResults.map((result, i) => (
                      <div key={i} className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                        result.success
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center space-x-2">
                          {result.success
                            ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                            : <AlertCircle className="h-4 w-4 text-red-600" />
                          }
                          <span className={`font-medium text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                            IDRegra: {result.idRegra}
                          </span>
                        </div>
                        <span className={`text-sm ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                          {result.success ? 'Salvo com sucesso' : result.error}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {file && importResult && !parsing && !sending && sendResults.length === 0 && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {hasErrors
                  ? 'Corrija os erros antes de importar'
                  : `${importResult.promocoes.length} ${importResult.promocoes.length === 1 ? 'promoção pronta' : 'promoções prontas'} para importação`
                }
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleImport}
                  disabled={hasErrors || importResult.promocoes.length === 0}
                  className="flex items-center space-x-2 px-6 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="h-4 w-4" />
                  <span>Importar {importResult.promocoes.length} {importResult.promocoes.length === 1 ? 'promoção' : 'promoções'}</span>
                </button>
              </div>
            </div>
          )}

          {sendResults.length > 0 && !sending && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-end">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
              >
                Fechar
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
